import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  fetchCartItems, 
  selectCartState, 
  selectIsUserLoggedIn, 
  selectIsAdminLoggedIn,
  selectSelectedAddress,
  selectUser,
  selectWithcookieData,
  registerCheckout,
  fetchWithcookieData
} from '../../../redux/WithcookieSlice';
import AddressSearchPopup from '../../common/AddressSearchPopup';
import { PriceDisplay } from '../../common/PriceDisplay';
import { useTranslation } from '../../../redux/TranslationHelper';
import './Payment.css';
import { PaymentProps, CartItem, PaymentFormData, OrderData, PaymentRequestData, PaymentError } from './Payment.types';
import { adaptPaymentProps } from './Payment.adapter';

// 토스페이먼츠 스크립트 로드 상태 관리
let tossScriptLoaded = false;
let tossScriptLoading = false;

// 토스페이먼츠 SDK 스크립트를 미리 로드하는 함수
const preloadTossPaymentsScript = (): Promise<void> => {
  if (tossScriptLoaded || tossScriptLoading) {
    return Promise.resolve();
  }

  tossScriptLoading = true;
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://js.tosspayments.com/v1/payment';
    script.async = true;
    
    script.onload = () => {
      tossScriptLoaded = true;
      tossScriptLoading = false;
      resolve();
    };
    
    script.onerror = (error) => {
      tossScriptLoading = false;
      reject(error);
    };
    
    document.body.appendChild(script);
  });
};

const PaymentComponent: React.FC<PaymentProps> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = adaptPaymentProps(props);
  const { t } = useTranslation();
  
  // 컴포넌트 속성 추출
  const { 
    className = "payment-component-wrapper padding-tb-40",
    style = {},
    componentProps = {}
  } = adaptedProps;
  
  const {
    title = t("결제하기"),
    emptyText = t("장바구니가 비어 있습니다."),
    shippingFee = 4000 // 기본 배송비
  } = componentProps;
  
  // any 타입으로 디스패치 정의 (타입 오류 회피)
  const dispatch = useDispatch() as any;
  const navigate = useNavigate();
  
  // Redux 상태 선택
  const cartState = useSelector(selectCartState);
  const cartItems = cartState.cartItems || [];
  const loading = cartState.loading;
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  const selectedAddress = useSelector(selectSelectedAddress);
  const user = useSelector(selectUser);
  const withcookieData = useSelector(selectWithcookieData);
  
  // 상태 정의
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [showAddressPopup, setShowAddressPopup] = useState<boolean>(false);
  const [formData, setFormData] = useState<PaymentFormData>({
    name: '',
    phone: '',
    email: '',
    address: '',
    addressDetail: '',
    zipcode: '',
    paymentMethod: 'CARD',
    agreeTerms: false
  });

  // 회사 설정에서 무료배송 기준금액 가져오기
  const freeShippingLimit = useMemo(() => {
    try {
      // withcookieData에서 companySettingJson 값 찾기
      let companySettingJson = null;
      
      // 서버에서 받은 데이터 구조에 따라 여러 경로 확인
      if (withcookieData?.company?.companySettingJson) {
        companySettingJson = withcookieData.company.companySettingJson;
      } else if (withcookieData?.skin?.company?.companySettingJson) {
        companySettingJson = withcookieData.skin.company.companySettingJson;
      }
      
      // JSON 문자열 또는 객체 처리
      if (typeof companySettingJson === 'string') {
        const parsedSettings = JSON.parse(companySettingJson);
        return parsedSettings.free_shipping_limit || 50000; // 기본값 50000
      } else if (companySettingJson && typeof companySettingJson === 'object') {
        return companySettingJson.free_shipping_limit || 50000; // 기본값 50000
      }
      
      return 50000; // 기본값
    } catch (error) {
      console.error(t('무료배송 기준금액 가져오기 오류:'), error);
      return 50000; // 에러 발생 시 기본값
    }
  }, [withcookieData, t]);

  // 토스페이먼츠 결제창을 띄우는 함수
  const openTossPayments = useCallback((paymentData: PaymentRequestData): void => {
    // 스크립트 로드 여부 확인을 위한 변수
    const hasScript = !!document.querySelector('script[src="https://js.tosspayments.com/v1/payment"]');
    
    // 토스페이먼츠 SDK 스크립트가 로드되어 있지 않은 경우 로드
    if (!(window as any).TossPayments && !hasScript) {
      const script = document.createElement('script');
      script.src = 'https://js.tosspayments.com/v1/payment';
      script.async = true;
      script.onload = () => {
        // 100ms 지연 후 결제창 열기 (스크립트 초기화 시간 확보)
        setTimeout(() => {
          initTossPayments(paymentData);
        }, 100);
      };
      script.onerror = (error) => {
        console.error(t('[토스페이먼츠] 스크립트 로드 실패'), error);
        alert(t('결제 모듈을 불러오는데 실패했습니다. 잠시 후 다시 시도해주세요.'));
      };
      document.body.appendChild(script);
    } else {
      // 이미 로드되어 있으면 바로 결제창 열기
      
      // window.TossPayments가 없지만 스크립트는 있는 경우 지연 후 시도
      if (!(window as any).TossPayments && hasScript) {
        setTimeout(() => {
          if ((window as any).TossPayments) {
            initTossPayments(paymentData);
          } else {
            console.error(t('[토스페이먼츠] TossPayments 객체를 찾을 수 없음, 페이지 새로고침 필요'));
            alert(t('결제 모듈을 초기화할 수 없습니다. 페이지를 새로고침한 후 다시 시도해주세요.'));
          }
        }, 500);
      } else {
        // TossPayments 객체가 정상적으로 있는 경우
        initTossPayments(paymentData);
      }
    }
  }, [t]);

  // 토스페이먼츠 결제창 초기화 및 열기
  const initTossPayments = useCallback((paymentData: PaymentRequestData): void => {
    try {
      // 전역 객체 확인
      if (typeof (window as any).TossPayments === 'undefined') {
        console.error(t('[토스페이먼츠] window.TossPayments 객체가 없습니다'));
        alert(t('결제 모듈을 불러오는데 실패했습니다. 페이지를 새로고침 후 다시 시도해주세요.'));
        return;
      }
      
      // paymentData에서 클라이언트 키 가져오기
      const clientKey = paymentData.clientKey || 'test_ck_D5GePWvyJnrK0W0k6q8gLzN97Eoq';
      
      // TossPayments 인스턴스 생성
      const tossPayments = (window as any).TossPayments(clientKey);
      
      if (!tossPayments) {
        console.error(t('[토스페이먼츠] TossPayments 인스턴스 생성 실패'));
        alert(t('결제 모듈 초기화에 실패했습니다. 다시 시도해주세요.'));
        return;
      }
      
      // 결제 방법에 따른 paymentType 값 설정
      const paymentType = paymentData.method === 'card' ? 'CARD' : 
                         paymentData.method === 'transfer' ? 'SEND' : 
                         paymentData.method === 'virtualAccount' ? 'NONE' : 'CARD';
      
      // 결제창 호출
      tossPayments.requestPayment(paymentData.method, {
        amount: paymentData.amount,
        orderId: paymentData.orderId,
        orderName: paymentData.orderName,
        customerName: paymentData.customerName || t('구매자'),
        customerEmail: paymentData.customerEmail || '',
        successUrl: `${window.location.origin}/payment/success?paymentType=${paymentType}`,
        failUrl: `${window.location.origin}/payment/fail`,
      }).catch((error: PaymentError) => {
        console.error(t('[토스페이먼츠] 결제창 열기 실패'), error);
        
        // 오류 코드별 메시지 처리
        const errorMessage = getPaymentErrorMessage(error);
        
        if (error.code === 'USER_CANCEL') {
          // 사용자가 결제창을 닫은 경우
        } else {
          // 그 외 에러 처리
          console.error(t('결제 에러:'), error);
          alert(errorMessage);
        }
      });
    } catch (error) {
      console.error(t('[토스페이먼츠] 예외 발생'), error);
      alert(t(`결제 과정 중 오류가 발생했습니다: ${(error as Error).message || '알 수 없는 오류'}`));
    }
  }, [t]);

  // 토스페이먼츠 오류 코드에 따른 사용자 친화적인 메시지 반환
  const getPaymentErrorMessage = useCallback((error: PaymentError): string => {
    if (!error || !error.code) {
      return t('결제 중 알 수 없는 오류가 발생했습니다. 다시 시도해주세요.');
    }
    
    // 오류 코드별 메시지 매핑
    const errorMessages: Record<string, string> = {
      'USER_CANCEL': t('결제가 취소되었습니다.'),
      'INVALID_CARD_COMPANY': t('지원하지 않는 카드사입니다.'),
      'INVALID_CARD_NUMBER': t('유효하지 않은 카드번호입니다.'),
      'INVALID_CARD_EXPIRY': t('유효기간이 올바르지 않습니다.'),
      'INVALID_CARD_EXPIRY_YEAR': t('카드 유효기간(년)이 올바르지 않습니다.'),
      'INVALID_CARD_EXPIRY_MONTH': t('카드 유효기간(월)이 올바르지 않습니다.'),
      'INVALID_CARD_PASSWORD': t('카드 비밀번호가 올바르지 않습니다.'),
      'INVALID_CARD_INSTALLMENT_PLAN': t('유효하지 않은 할부 개월 수입니다.'),
      'INVALID_CARD_CVC': t('유효하지 않은 CVC입니다.'),
      'INVALID_CARD_IDENTITY': t('유효하지 않은 생년월일/사업자등록번호입니다.'),
      'INVALID_CARD_TYPE': t('유효하지 않은 카드 종류입니다.'),
      'INVALID_AMOUNT': t('유효하지 않은 금액입니다.'),
      'INVALID_ORDER_ID': t('유효하지 않은 주문번호입니다.'),
      'PAYMENT_WINDOW_CLOSED': t('결제 창이 닫혔습니다. 다시 시도해주세요.'),
      'BROWSER_NOT_SUPPORTED': t('지원하지 않는 브라우저입니다. 다른 브라우저로 시도해주세요.'),
      'NOT_SUPPORTED_PAYMENT': t('지원하지 않는 결제수단입니다.'),
      'NETWORK_ERROR': t('네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'),
      'PAYMENT_CREATION_FAIL': t('결제 생성에 실패했습니다. 다시 시도해주세요.'),
      'PAY_PROCESS_CANCELED': t('결제가 취소되었습니다.'),
      'PAY_PROCESS_ABORTED': t('결제가 중단되었습니다.')
    };
    
    return errorMessages[error.code] || t(`결제 오류가 발생했습니다 (${error.code}): ${error.message || '알 수 없는 오류'}`);
  }, [t]);

  // 선택된 주소가 변경되면 폼 데이터 업데이트
  useEffect(() => {
    if (selectedAddress) {
      setFormData(prev => ({
        ...prev,
        address: selectedAddress.road || selectedAddress.jibun || '',
        zipcode: selectedAddress.postalCode || ''
      }));
    }
  }, [selectedAddress]);

  // 컴포넌트 마운트 시 토스페이먼츠 스크립트 미리 로드 및 장바구니 정보 로드
  useEffect(() => {
    // 스크립트 사전 로드
    preloadTossPaymentsScript().catch(error => {
      console.warn(t('[토스페이먼츠] 사전 스크립트 로드 실패, 결제 시 다시 시도합니다'), error);
    });
    
    // 장바구니 정보 로드
    if (isUserLoggedIn || isAdminLoggedIn) {
      // 장바구니 정보 로드
      dispatch(fetchCartItems());
      
      // 회사 정보 등 현재 상태 로드 (필요한 경우에만)
      if (!withcookieData) {
        dispatch(fetchWithcookieData());
      }
    }
  }, [dispatch, isUserLoggedIn, isAdminLoggedIn, withcookieData, t]);

  // 직급별 가격 계산 함수
  const calculateLevelPrice = useCallback((basePrice: number, product: any) => {
    // 사용자 정보가 없거나 로그인하지 않은 경우
    if (!user || !isUserLoggedIn) {
      return {
        originalPrice: basePrice,
        levelPrice: basePrice,
        discount: 0,
        discountRate: 0,
        levelName: null
      };
    }
    
    // 사용자의 level2 ID 확인
    const userLevel2Id = user.level2;
    const userLevel2Policy = user.level2Policy;
    
    // level2가 없으면 할인 없음
    if (!userLevel2Id || !userLevel2Policy) {
      return {
        originalPrice: basePrice,
        levelPrice: basePrice,
        discount: 0,
        discountRate: 0,
        levelName: null
      };
    }
    
    // 상품의 optionJson에서 level2_price 확인
    let levelPrice = basePrice;
    if (product?.optionJson?.level2_price && product.optionJson.level2_price[userLevel2Id]) {
      // level2_price에 해당 레벨의 가격이 있으면 그 가격 사용
      levelPrice = product.optionJson.level2_price[userLevel2Id];
    } else if (userLevel2Policy?.settingJson?.supply_price_ratio) {
      // level2_price가 없으면 supply_price_ratio 사용
      const supplyPriceRatio = userLevel2Policy.settingJson.supply_price_ratio;
      levelPrice = Math.floor(basePrice * supplyPriceRatio);
    }
    
    const discount = basePrice - levelPrice;
    const discountRate = basePrice > 0 ? Math.round((discount / basePrice) * 100) : 0;
    
    return {
      originalPrice: basePrice,
      levelPrice,
      discount,
      discountRate,
      levelName: userLevel2Policy.customName || userLevel2Policy.type
    };
  }, [user, isUserLoggedIn]);

  // 상품 가격 합계 계산
  const calculateTotalPrice = useCallback((): number => {
    return cartItems.reduce((sum: number, item: CartItem) => {
      const basePrice = item.product?.config?.discounted_price 
        ? Number(item.product.config.discounted_price) 
        : item.product?.config?.default_price 
          ? Number(item.product.config.default_price) 
          : 0;
      
      // 직급별 가격 계산
      const priceInfo = calculateLevelPrice(basePrice, item.product);
      const finalBasePrice = priceInfo.levelPrice;
      
      // Variant 추가 가격 포함
      const additionalPrice = item.variant?.additionalPrice 
        ? Number(item.variant.additionalPrice) 
        : 0;
      
      const totalPrice = finalBasePrice + additionalPrice;
      
      return sum + (totalPrice * (item.count || 1));
    }, 0);
  }, [cartItems, calculateLevelPrice]);

  // 총 금액 계산 (상품 가격 + 배송비)
  const calculateGrandTotal = useCallback((): number => {
    const totalPrice = calculateTotalPrice();
    // 주문 금액이 무료배송 기준금액보다 크거나 같으면 배송비 무료(0원)
    const appliedShippingFee = totalPrice >= freeShippingLimit ? 0 : shippingFee;
    return totalPrice + appliedShippingFee;
  }, [calculateTotalPrice, shippingFee, freeShippingLimit]);

  // 폼 입력 처리
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  // 우편번호 검색 버튼 클릭 핸들러
  const handleAddressSearch = useCallback((): void => {
    setShowAddressPopup(true);
  }, []);

  // 결제 처리
  const handlePayment = useCallback(async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // 폼 검증
    if (!formData.name || !formData.phone || !formData.address || !formData.zipcode) {
      alert(t('필수 입력 항목을 모두 입력해주세요.'));
      return;
    }
    
    if (!formData.agreeTerms) {
      alert(t('결제 및 개인정보 처리방침에 동의해주세요.'));
      return;
    }
    
    // 장바구니가 비어있는지 확인
    if (!cartItems || cartItems.length === 0) {
      alert(t('구매할 상품이 장바구니에 없습니다.'));
      return;
    }
    
    try {
      // 사용자 정보 확인
      if (!user || !user.id) {
        console.error(t("[결제 오류] 사용자 정보 없음:"), user);
        alert(t("로그인 정보를 확인할 수 없습니다. 다시 로그인해주세요."));
        return;
      }
      
      // 회사 정보가 없는 경우 새로 불러오기
      if (!withcookieData) {
        try {
          await dispatch(fetchWithcookieData());
        } catch (error) {
          console.warn(t("[결제] 회사 정보를 불러오는데 실패했습니다:"), error);
        }
      }
      
      // API 응답에서 회사 ID 가져오기 (여러 경로 확인)
      const companyId = withcookieData?.company?.id || 
                       withcookieData?.skin?.companyId || 
                       withcookieData?.skin?.company?.id;
      
      if (!companyId) {
        console.error(t("[결제 오류] 회사 ID를 찾을 수 없습니다:"), withcookieData);
        alert(t('회사 정보를 불러올 수 없습니다. 페이지를 새로고침하거나 나중에 다시 시도해주세요.'));
        return;
      }
      
      // 주문 ID 생성 (현재 시간과 랜덤 문자열 조합)
      const orderId = `ORDER_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      
      // 상품명 생성 (첫 번째 상품명 + 추가 상품 개수)
      const firstItem = cartItems[0]?.product?.product_name || 
                       cartItems[0]?.product?.title || 
                       t('상품');
      const orderName = cartItems.length > 1 
        ? `${t(firstItem)} ${t('외')} ${cartItems.length - 1}${t('건')}` 
        : t(firstItem);
      
      const totalPrice = calculateTotalPrice();
      // 주문 금액이 무료배송 기준금액보다 크거나 같으면 배송비 무료(0원)
      const appliedShippingFee = totalPrice >= freeShippingLimit ? 0 : shippingFee;
      const grandTotal = totalPrice + appliedShippingFee;
      
      // 회사 정보 확인 (check_current API에서 가져온 값 사용)
      const skinId = withcookieData?.skin?.id;
      const userId = user.id;
      
      // 장바구니 아이템 구성
      const cartItems_formatted = cartItems.map((item: CartItem) => {
        // 상품 ID 찾기 (우선순위에 따라 검색)
        let productId = null;
        
        if (item.product_id) {
          // 1. API에서 반환된 product_id가 있는 경우
          productId = item.product_id;
        } else if (item.product && item.product.id) {
          // 2. 상품 객체에 직접 id가 있는 경우
          productId = item.product.id;
        } else if (item.product && item.product.product_id) {
          // 3. 상품 객체에 product_id가 있는 경우
          productId = item.product.product_id;
        } else if (item.id) {
          // 4. 아이템 자체에 id가 있는 경우
          productId = item.id;
        }
        
        if (!productId) {
          console.error(t("[결제 오류] 상품 ID를 찾을 수 없습니다:"), item);
          throw new Error(t("상품 정보가 올바르지 않습니다."));
        }
        
        // 숫자로 변환
        const numericProductId = Number(productId);
        if (isNaN(numericProductId) || numericProductId <= 0) {
          console.error(t("[결제 오류] 유효하지 않은 상품 ID:"), productId);
          throw new Error(t("상품 ID가 유효하지 않습니다."));
        }
        
        return {
          product_id: numericProductId,
          company_id: companyId,
          user_id: userId,
          count: item.count || 1
        };
      });
      
      // 빈 상품 목록 체크
      if (cartItems_formatted.length === 0 || cartItems_formatted.some(item => !item.product_id)) {
        console.error(t("[결제 오류] 장바구니 항목 형식화 실패"));
        throw new Error(t("구매할 상품을 찾을 수 없습니다."));
      }
      
      // 장바구니 아이템 ID 목록 추출 (cart_ids 필드로 사용)
      const cartIds = cartItems.map(item => item.id).filter(id => id);
      if (!cartIds.length) {
        console.error(t("[결제 오류] 장바구니 ID가 없습니다:"), cartItems);
        throw new Error(t("장바구니 정보가 올바르지 않습니다."));
      }
 
      // 서버 요구 형식에 맞게 주문 등록 데이터 준비
      const orderData = {
        // 필수 사용자 정보
        user_id: userId,
        company_id: companyId,
        skin_id: skinId || 0,  // skin_id가 undefined이면 0으로 설정
        
        // 수신자 정보
        receiver_name: formData.name,
        receiver_phone: formData.phone,
        receiver_email: formData.email || '',
        
        // 금액 정보
        total_price: grandTotal,
        buy_price: grandTotal,
        parcel_price: totalPrice,
        
        // 결제 방법
        payment_method: formData.paymentMethod,
        
        // 배송지 정보
        address: formData.address,
        address_detail: formData.addressDetail || '',
        postal_code: formData.zipcode,
        
        // 장바구니 아이템
        carts: cartItems_formatted,
        
        // 장바구니 ID 목록 (API에서 필요)
        cart_ids: cartIds
      } as OrderData;

      
      // 주문 등록 API 호출
      const result = await dispatch(registerCheckout(orderData as any)).unwrap();
      

      
      // 성공 여부 확인 - statusCode가 200이고 checkout 객체가 있는지 확인
      const isSuccess = result && (
        result.statusCode === 200 || 
        result.statusCode === 201 || 
        result.status === 201 || 
        result.checkout
      );
      
      if (!isSuccess) {
        console.error(t("[결제 오류] 주문 등록 실패:"), result);
        throw new Error(result?.message || t('주문 등록에 실패했습니다.'));
      }
      
      // 등록된 주문 정보 확인 (서버에서 반환한 orderId가 있으면 사용)
      const registeredOrderId = result.checkout?.id 
        ? `ORDER_${result.checkout.id}` 
        : result.orderId || orderId;
      
      // 토스페이먼츠용 결제 방법 맵핑
      let tossPaymentMethod = 'card';
      if (formData.paymentMethod === 'SEND') {
        tossPaymentMethod = 'transfer';
      } else if (formData.paymentMethod === 'NONE') {
        tossPaymentMethod = 'virtualAccount'; // 가상계좌는 virtualAccount로 설정
      }
      
      // 토스 결제 키 가져오기 및 검증
      const tossClientKey = withcookieData?.skin?.company?.tossKey;
      if (!tossClientKey) {
        console.error(t("[결제 오류] 토스 결제 키가 없습니다:"), withcookieData);
        throw new Error(t("결제 설정이 올바르지 않습니다. 관리자에게 문의하세요."));
      }
      
      // 결제 요청 데이터 구성
      const paymentData: PaymentRequestData = {
        method: tossPaymentMethod, // 토스페이먼츠 결제창에 맞는 결제 방법으로 변환
        amount: grandTotal,
        orderId: registeredOrderId,
        orderName: orderName,
        customerName: formData.name,
        customerEmail: formData.email || '',
        // withcookieData에서 토스 결제 키 가져오기
        clientKey: tossClientKey,
      };

      
      // 토스페이먼츠 결제창 열기
      openTossPayments(paymentData);
      
    } catch (error) {
      console.error(t('[결제 오류]'), error);
      alert(t(`결제 처리 중 오류가 발생했습니다: ${(error as Error).message || '알 수 없는 오류'}`));
    }
  }, [cartItems, calculateTotalPrice, dispatch, formData, openTossPayments, shippingFee, t, user, withcookieData, freeShippingLimit]);

  // 로딩 상태 표시
  if (loading) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="payment-loading">
                <p>{t("장바구니 정보를 불러오는 중입니다...")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 로그인 필요
  if (!isUserLoggedIn && !isAdminLoggedIn) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="payment-login-required">
                <p>{t("결제를 진행하려면 로그인이 필요합니다.")}</p>
                <a href="/login" className="login-button">{t("로그인")}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 장바구니가 비어있는 경우
  if (!cartItems || cartItems.length === 0) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="payment-empty">
                <h2>{title}</h2>
                <p>{emptyText}</p>
                <a href="/shopping" className="continue-shopping-button">
                  {t("쇼핑 계속하기")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalPrice = calculateTotalPrice();
  // 주문 금액이 무료배송 기준금액보다 크거나 같으면 배송비 무료(0원)
  const appliedShippingFee = totalPrice >= freeShippingLimit ? 0 : shippingFee;
  const grandTotal = totalPrice + appliedShippingFee;

  return (
    <div className={className} style={style}>
      {showAddressPopup && (
        <AddressSearchPopup onClose={() => setShowAddressPopup(false)} />
      )}
      
      <div className="container">
        <div className="row">
          <div className="col-12">
            <h2 className="payment-title">{title}</h2>
          </div>
        </div>
        
        <div className="row" style={{ margin:"0 auto" }}>
          {/* 주문 정보 및 결제 폼 */}
          <div className="col-lg-8 col-md-12">
            <div className="payment-form-wrapper">
              <h3 className="form-section-title">{t("주문자 정보")}</h3>
              <form onSubmit={handlePayment} className="payment-form">
                <div className="form-group">
                  <label htmlFor="name">{t("이름")} <span className="required">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">{t("휴대폰 번호")} <span className="required">*</span></label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder={t("010-0000-0000")}
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">{t("이메일")}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>

                <h3 className="form-section-title mt-4">{t("배송지 정보")}</h3>
                
                <div className="form-group">
                  <label htmlFor="zipcode">{t("우편번호")} <span className="required">*</span></label>
                  <div className="zipcode-group">
                    <input
                      type="text"
                      id="zipcode"
                      name="zipcode"
                      value={formData.zipcode}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                      readOnly
                    />
                    <button 
                      type="button" 
                      className="search-zipcode-btn"
                      onClick={handleAddressSearch}
                    >
                      {t("우편번호 찾기")}
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">{t("주소")} <span className="required">*</span></label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                    readOnly
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="addressDetail">{t("상세주소")}</label>
                  <input
                    type="text"
                    id="addressDetail"
                    name="addressDetail"
                    value={formData.addressDetail}
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>

                <h3 className="form-section-title mt-4">{t("결제 방법")}</h3>
                
                <div className="payment-methods-container">
                  <div className={`payment-method-card ${formData.paymentMethod === 'CARD' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      id="payment-card"
                      name="paymentMethod"
                      value="CARD"
                      checked={formData.paymentMethod === 'CARD'}
                      onChange={handleInputChange}
                      className="payment-method-input"
                    />
                    <label htmlFor="payment-card" className="payment-method-label">
                      <div className="payment-method-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                          <line x1="1" y1="10" x2="23" y2="10"></line>
                        </svg>
                      </div>
                      <div className="payment-method-info">
                        <span className="payment-method-name">{t("신용카드")}</span>
                        <span className="payment-method-desc">{t("모든 종류의 신용/체크카드")}</span>
                      </div>
                    </label>
                  </div>
                  
                  <div className={`payment-method-card ${formData.paymentMethod === 'NONE' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      id="payment-vbank"
                      name="paymentMethod"
                      value="NONE"
                      checked={formData.paymentMethod === 'NONE'}
                      onChange={handleInputChange}
                      className="payment-method-input"
                    />
                    <label htmlFor="payment-vbank" className="payment-method-label">
                      <div className="payment-method-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                          <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </svg>
                      </div>
                      <div className="payment-method-info">
                        <span className="payment-method-name">{t("계좌 이체")}</span>
                        <span className="payment-method-desc">{t("무통장 입금")}</span>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="terms-agreement mt-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      name="agreeTerms"
                      checked={formData.agreeTerms}
                      onChange={handleInputChange}
                      className="form-check-input"
                      required
                    />
                    <label htmlFor="agreeTerms" className="form-check-label">
                      {t("결제 진행 및 개인정보 처리방침에 동의합니다")} <span className="required">*</span>
                    </label>
                  </div>
                </div>

                <div className="payment-button-container mt-4">
                  <button type="submit" className="payment-button">
                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                    <PriceDisplay price={grandTotal} /> {t("결제하기")}
                  </button>
                </div>
              </form>
            </div>
          </div>
          
          {/* 주문 요약 정보 */}
          <div className="col-lg-4 col-md-12">
            <div className="order-summary">
              <h3 className="summary-title">{t("주문 내역")}</h3>
              
              <div className="order-items">
                {cartItems && cartItems.length > 0 ? (
                  <ul className="order-item-list">
                    {cartItems.map((item: CartItem, index: number) => (
                      <li key={index} className="order-item">
                        <div className="order-item-image">
                          {item.product?.config?.img_url ? (
                            <img
                              src={item.product.config.img_url}
                              alt={item.product.title}
                            />
                          ) : (
                            <div className="no-image">{t("이미지 없음")}</div>
                          )}
                        </div>
                        <div className="order-item-info">
                          <p className="item-name">
                            {t(item.product?.title) || t('상품명 없음')}
                          </p>
                          
                          {/* 옵션 정보 표시 */}
                          {item.variant && item.variant.options && item.variant.options.length > 0 && (
                            <p className="item-options">
                              {item.variant.options.map((opt, index) => (
                                <span key={index}>
                                  {opt.optionValue.optionGroup.name}: {opt.optionValue.value}
                                  {index < (item.variant?.options?.length || 0) - 1 && ', '}
                                </span>
                              ))}
                            </p>
                          )}
                          
                          {/* 레거시 옵션 정보 표시 */}
                          {!item.variant && item.options && Object.keys(item.options).length > 0 && (
                            <p className="item-options">
                              {(() => {
                                const optionEntries = Object.entries(item.options)
                                  .filter(([key]) => !['sku', 'price', 'pv'].includes(key));
                                
                                if (optionEntries.length === 0) return null;
                                
                                return optionEntries.map(([key, value], index) => (
                                  <span key={key}>
                                    {key}: {value}
                                    {index < optionEntries.length - 1 && ', '}
                                  </span>
                                ));
                              })()}
                            </p>
                          )}
                          
                          <p className="item-quantity">
                            {t("수량")}: {item.count || 1}{t("개")}
                          </p>
                          <p className="item-price">
                            {(() => {
                              const basePrice = item.product?.config?.discounted_price 
                                ? Number(item.product.config.discounted_price) 
                                : item.product?.config?.default_price 
                                  ? Number(item.product.config.default_price) 
                                  : 0;
                              
                              // 직급별 가격 계산
                              const priceInfo = calculateLevelPrice(basePrice, item.product);
                              const finalBasePrice = priceInfo.levelPrice;
                              
                              const additionalPrice = item.variant?.additionalPrice 
                                ? Number(item.variant.additionalPrice) 
                                : 0;
                              
                              const totalPrice = (finalBasePrice + additionalPrice) * (item.count || 1);
                              
                              return (
                                <>
                                  {/* 직급별 가격이 있는 경우 */}
                                  {priceInfo.discount > 0 && priceInfo.levelName && (
                                    <span className="level-info-small">
                                      <span className="level-badge-tiny">{priceInfo.levelName}</span>
                                    </span>
                                  )}
                                  {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                                  <PriceDisplay price={totalPrice} />
                                  {additionalPrice > 0 && (
                                    <span className="additional-price-info">
                                      (옵션 +₩{additionalPrice.toLocaleString()})
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-items">{t("주문 내역이 없습니다.")}</p>
                )}
              </div>
              
              <div className="order-totals">
                <div className="order-total-item">
                  <span>{t("상품 금액")}</span>
                  <span>
                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                    <PriceDisplay price={totalPrice} />
                  </span>
                </div>
                <div className="order-total-item">
                  <div className="item-label">{t("배송비")}</div>
                  <div className="item-value">
                    {totalPrice >= freeShippingLimit ? (
                      <span className="free-shipping">{t("무료배송")}</span>
                    ) : (
                      /* @ts-ignore - PriceDisplay는 price prop을 받는 컴포넌트입니다 */
                      <PriceDisplay price={appliedShippingFee} />
                    )}
                  </div>
                </div>
                <div className="order-total-item grand-total">
                  <span>{t("총 결제금액")}</span>
                  <span>
                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                    <PriceDisplay price={grandTotal} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentComponent; 